import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const ScheduleM3Form1120S = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Schedule M-3 (1120-S): Filing & Reconciliation Guide"
          description="Learn Schedule M-3 (Form 1120-S) requirements, reconciliation rules, filing thresholds, and compliance tips for S corporations with $10M+ assets."
          keywords=""
          canonical="resources/business-tax/schedule-m3-form-1120s/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Schedule M-3 (Form 1120-S):</span>
          </span>{' '}
          <span style={{ position: 'relative', zIndex: 9 }}>
            Complete IRS Guide to Net Income (Loss) Reconciliation for S
            Corporations
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/schedule-m3-form-1120s/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule M-3 (Form 1120-S) at a Glance
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 2 }}>
                    It provides a detailed reconciliation between an S
                    corporation's{' '}
                    <span style={{ fontWeight: 600 }}>
                      financial statement income (loss)
                    </span>{' '}
                    and{' '}
                    <span style={{ fontWeight: 600 }}>
                      taxable income (loss)
                    </span>{' '}
                    reported on Form 1120-S.
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>Who Must File:</span>{' '}
                        S corporations with{' '}
                        <span style={{ fontWeight: 600 }}>
                          $10 million or more in total assets
                        </span>{' '}
                        generally must file Schedule M-3.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          Under $10 Million:
                        </span>{' '}
                        S corporations with less than{' '}
                        <span style={{ fontWeight: 600 }}>
                          $10 million in assets
                        </span>{' '}
                        generally use Schedule M-1 but may file Schedule M-3
                        voluntarily.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          $50 Million or More:
                        </span>{' '}
                        S corporations with{' '}
                        <span style={{ fontWeight: 600 }}>
                          $50 million or more in assets
                        </span>{' '}
                        must complete Schedule M-3 in full, including detailed
                        book-to-tax differences.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Schedule M-3 (
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Form 1120-S
                  </Link>
                  ) provides a detailed reconciliation between an S
                  corporation&apos;s financial statement income and the taxable
                  income reported to the IRS. This guide explains when Schedule
                  M-3 is required, how the reconciliation works, and the key
                  reporting rules corporations must follow to ensure accurate
                  tax compliance.
                </Typography>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schm3" sx={{ color: '#0202ff' }}>
                          What is Schedule M-3?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Must File Schedule M-3?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line" sx={{ color: '#0202ff' }}>
                          Line-by-Line Instructions for Schedule M-3
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part1" sx={{ color: '#0202ff' }}>
                                Part I: Financial Information and Net Income
                                (Loss) Reconciliation
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part2" sx={{ color: '#0202ff' }}>
                                Part II: Reconciliation of Net Income (Loss) per
                                Income Statement of the Corporation with Total
                                Income (Loss) per Return
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part3" sx={{ color: '#0202ff' }}>
                                Part III: Reconciliation of Expense/Deduction
                                Items
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#structure" sx={{ color: '#0202ff' }}>
                          Schedule M-3 vs Schedule M-1
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#book" sx={{ color: '#0202ff' }}>
                          Common Book-to-Tax Adjustments
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Filing Mistakes
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" sx={{ color: '#0202ff' }}>
                          FAQs
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schm3"
                >
                  What is Schedule M-3?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    Schedule M-3 (Form 1120-S)
                  </span>{' '}
                  is a detailed reconciliation schedule used by S corporations
                  with significant assets to explain how{' '}
                  <span style={{ fontWeight: 600 }}>
                    financial statement net income (loss)
                  </span>{' '}
                  converts into{' '}
                  <span style={{ fontWeight: 600 }}>taxable income (loss)</span>{' '}
                  reported on{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Form 1120-S
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The schedule replaces simplified reconciliation methods by
                  providing a structured comparison between:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Financial accounting results</li>
                  <li>Temporary book-to-tax differences</li>
                  <li>Permanent tax adjustments</li>
                  <li>Final tax return amounts</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f1120ss3.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Schedule M-3
                  </Link>{' '}
                  increases transparency by allowing the IRS to evaluate how
                  accounting income is adjusted under tax rules.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who Must File Schedule M-3?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  An S corporation generally must attach Schedule M-3 if:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Total assets reported on{' '}
                    <Link
                      href="/resources/business-tax/schedule-l/"
                      sx={{ color: '#0202ff' }}
                    >
                      Schedule L
                    </Link>{' '}
                    equal or exceed{' '}
                    <span style={{ fontWeight: 600 }}>$10 million</span> at the
                    end of the tax year.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Corporations below this threshold typically use Schedule M-1
                  unless they choose to complete Schedule M-3 voluntarily.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Completion Rules Based on Asset Size
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Under $10M assets:</span>{' '}
                    Use <span style={{ fontWeight: 600 }}>Schedule M-1</span>;
                    Schedule M-3 is voluntary. If filed voluntarily, the
                    corporation may complete Part I while continuing Schedule
                    M-1, or choose to complete the full Schedule M-3.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      $10M or more (but under $50M):
                    </span>{' '}
                    Must complete{' '}
                    <span style={{ fontWeight: 600 }}>Schedule M-3</span>
                    for book-to-tax reconciliation reporting, providing detailed
                    disclosure of income, expense, and adjustment differences.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>$50M or more:</span> Must
                    complete{' '}
                    <span style={{ fontWeight: 600 }}>Schedule M-3</span> in
                    full, including expanded reporting of temporary and
                    permanent differences between financial statement income and
                    taxable income.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These thresholds determine the level of detail required in
                  reconciliation reporting.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="line"
                >
                  Line-by-Line Instruction for Schedule M-3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before completing this part of Schedule M-3, Enter the
                  following details at the top of each page of the form:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Corporation Name:</span>{' '}
                    Enter the legal name of the S Corporation exactly as shown
                    on Form 1120-S.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Employer Identification Number (EIN):
                    </span>{' '}
                    Enter the corporation's nine-digit EIN issued by the IRS.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part1"
                >
                  Part I: Financial Information and Net Income (Loss)
                  Reconciliation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schm3-1120s-part1-img.webp" />
                  <source srcSet="/assets/images/schm3-1120s-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schm3-1120s-part1-img.png"
                    loading="lazy"
                    alt="Schedule M-3 Part I showing financial information and net income (loss) reconciliation lines 1a to 3b"
                    width={760}
                    height={415}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 1a: Certified Audited Non-Tax-Basis Income Statement
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Select Yes if your corporation prepared a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      certified audited financial statement
                    </strong>{' '}
                    (such as GAAP or IFRS) for the reporting period.{' '}
                  </li>
                  <li>
                    If multiple certified statements exist, use the one with the
                    highest IRS priority and complete Lines 2–11 using that
                    statement.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 1b: Non-Tax-Basis Income Statement
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    If you answered{' '}
                    <strong style={{ fontWeight: 600 }}>No</strong> on Line 1a,
                    indicate whether your corporation prepared any other{' '}
                    <strong style={{ fontWeight: 600 }}>
                      non-tax-basis financial statement
                    </strong>{' '}
                    by selecting the check box{' '}
                    <strong style={{ fontWeight: 600 }}>yes.</strong>
                  </li>
                  <li>
                    If no such statement exists, skip Lines 2 and 3, and report
                    the net income (loss) from your books and records on Line
                    4a.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 2: Income Statement Period
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      beginning and ending dates
                    </strong>{' '}
                    of the financial statement used to complete Schedule M-3.
                  </li>
                  <li>
                    The reporting period must end with or fall within the
                    corporation's current tax year.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 3a: Restated Income Statement (Current Period)
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Select <strong style={{ fontWeight: 600 }}>Yes</strong> if
                    the financial statement used for the current reporting
                    period has been restated.{' '}
                  </li>
                  <li>
                    Attach a brief explanation describing the reason for the
                    restatement and the original and revised net income amounts.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 3b: Restated Income Statement (Previous Five Periods)
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Select <strong style={{ fontWeight: 600 }}>Yes</strong> if
                    any financial statements for the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      previous five reporting periods
                    </strong>{' '}
                    were restated.{' '}
                  </li>
                  <li>
                    Include a short explanation for each restatement along with
                    the original and corrected net income figures.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/schm3-1120s-part1-line4-img.webp" />
                  <source srcSet="/assets/images/schm3-1120s-part1-line4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schm3-1120s-part1-line4-img.png"
                    loading="lazy"
                    alt="Schedule M-3 Part I showing financial information and net income (loss) reconciliation lines 4a to 11"
                    width={760}
                    height={418}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 4a: Worldwide Consolidated Net Income (Loss)
                </Typography>

                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the corporation's{' '}
                    <strong style={{ fontWeight: 600 }}>
                      worldwide consolidated net income (loss)
                    </strong>{' '}
                    from the financial statement selected in Line 1.
                  </li>
                  <li>
                    If no non-tax-basis financial statement was prepared, use
                    the net income (loss) from the corporation's books and
                    records.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 4b: Accounting Standard Used
                </Typography>

                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Select the accounting standard used to prepare the amount
                    reported on Line 4a.{' '}
                  </li>
                  <li>
                    Choose{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      GAAP, IFRS, tax basis, or other,
                    </strong>{' '}
                    whichever matches your financial statements.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 5a: Net Income from Nonincludible Foreign Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the net income of foreign entities included in your
                  financial statements but{' '}
                  <span style={{ fontWeight: 600 }}> not included</span> in your
                  U.S. tax return. Attach a statement identifying each entity
                  and the amount removed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 5b: Net Loss from Nonincludible Foreign Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the net loss from nonincludible foreign entities as a
                  <span style={{ fontWeight: 600 }}> positive amount</span>.
                  Include a supporting statement with details for each entity.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 6a: Net Income from Nonincludible U.S. Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the net income of U.S. entities that appear in your
                  financial statements but are{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    not part of the U.S. tax return.
                  </span>{' '}
                  Attach a statement listing each entity and its income.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 6b: Net Loss from Nonincludible U.S. Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the net loss from nonincludible U.S. entities as a
                  <span style={{ fontWeight: 600 }}> positive amount</span>.
                  Include a supporting statement explaining the adjustments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 7a: Other Foreign Disregarded Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the net income or loss of foreign disregarded entities
                  that are included in the tax return but not in the financial
                  statements. Attach a statement with the required entity
                  details.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 7b: Other U.S. Disregarded Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the income or loss of U.S. disregarded entities (other
                  than QSubs) that are not included in the consolidated
                  financial statements. Provide a supporting statement for each
                  entity.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 7c: Qualified Subchapter S Subsidiaries (QSubs)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the income or loss of QSubs that are included in the
                  tax return but excluded from the financial statements. Attach
                  a statement listing each QSub and its financial information.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 8: Adjustment to Elimination Entries
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter adjustments needed to reverse or add consolidation and
                  intercompany elimination entries. These adjustments ensure
                  only the correct entities and transactions are reflected in
                  the reconciliation.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 9: Adjustment to Reconcile Income Statement Period to Tax
                  Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your financial statement period differs from the tax year,
                  enter the adjustment needed to align the financial statement
                  income with the corporation's tax year. Attach an explanation
                  of the adjustment.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 10: Other Reconciliation Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any additional adjustments required to reconcile the
                  amount on Line 4a to the final net income reported on Line 11.
                  Attach a statement explaining each adjustment.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 11: Net Income (Loss) per Income Statement of the
                  Corporation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the corporation's final reconciled net income (or loss)
                  after all adjustments from Lines 5 through 10. This amount
                  should include only entities that are part of the U.S. tax
                  return.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 12: Total Assets and Liabilities of Included and Removed
                  Entities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schm3-1120s-part1-line12-img.webp" />
                  <source srcSet="/assets/images/schm3-1120s-part1-line12-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schm3-1120s-part1-line12-img.png"
                    loading="lazy"
                    alt="Schedule M-3 Part I Line 12 showing total assets and liabilities of included and removed entities"
                    width={760}
                    height={128}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 12a: Assets and Liabilities Included on Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total worldwide assets and liabilities of the
                  entities included in Line 4. Use the same financial statement
                  or books and records used for the income reported on Line 4a.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 12b: Assets and Liabilities Removed on Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total assets and liabilities of the nonincludible
                  foreign entities removed on Line 5. Report the full amounts,
                  not just the corporation's ownership share.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 12c: Assets and Liabilities Removed on Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total assets and liabilities of the nonincludible
                  U.S. entities removed on Line 6. Enter all values as positive
                  amounts.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 12d: Assets and Liabilities Included on Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the total assets and liabilities of the disregarded
                  entities and QSubs included on Line 7. Report the full entity
                  amounts as positive values.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part2"
                >
                  Part II: Reconciliation of Net Income (Loss) per Income
                  Statement of the Corporation with Total Income (Loss) per
                  Return
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schm3-1120s-part2-img.webp" />
                  <source srcSet="/assets/images/schm3-1120s-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schm3-1120s-part2-img.png"
                    loading="lazy"
                    alt="Schedule M-3 Part II showing reconciliation of net income (loss) per income statement with total income (loss) per return"
                    width={760}
                    height={781}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 1: Income (Loss) From Equity Method Foreign Corporations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the corporation's share of income or loss from foreign
                  corporations accounted for under the equity method. Complete
                  all applicable columns and attach a supporting statement with
                  the required details.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 2: Gross Foreign Dividends Not Previously Taxed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total gross foreign dividends that have not been
                  previously taxed. Report the amount in the appropriate columns
                  to reconcile book and tax differences.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 3: Subpart F, QEF, and Similar Income Inclusions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income recognized under Subpart F, Qualified Electing
                  Fund (QEF), or similar tax provisions. Include any temporary
                  or permanent differences between book and tax reporting.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 4: Gross Foreign Distributions Previously Taxed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter gross foreign distributions that were previously taxed
                  under U.S. tax rules. Report any reconciliation differences,
                  if applicable.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 5: Income (Loss) From Equity Method U.S. Corporations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the corporation's share of income or loss from U.S.
                  corporations accounted for under the equity method. Include
                  any book-to-tax adjustments in the appropriate columns.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 6: U.S. Dividends Not Eliminated in Tax Consolidation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report U.S. dividend income that was not eliminated in tax
                  consolidation. Record any related temporary or permanent
                  differences.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 7: Income (Loss) From U.S. Partnerships
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation's share of income or loss from U.S.
                  partnerships. Report the related reconciliation adjustments,
                  if required.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 8: Income (Loss) From Foreign Partnerships
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income or loss from foreign partnerships included in
                  the corporation's financial records. Complete the applicable
                  reconciliation columns.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 9: Income (Loss) From Other Pass-Through Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income or loss from other pass-through entities, such
                  as LLCs, trusts, or estates. Include any differences between
                  book income and taxable income.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 10: Items Relating to Reportable Transactions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income or loss from reportable transactions that
                  require IRS disclosure. Attach a supporting statement with the
                  necessary information.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 11: Interest Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report all interest income recognized during the tax year.
                  Include any temporary or permanent differences between
                  financial statement and tax reporting.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 12: Total Accrual to Cash Adjustment
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the adjustment required when reconciling accrual-basis
                  financial statements to a cash-basis tax return. Report the
                  net adjustment amount.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 13: Hedging Transactions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income or loss from hedging transactions. Include any
                  book-to-tax differences resulting from these transactions.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 14: Mark-to-Market Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income or loss recognized under the mark-to-market
                  method. Enter any temporary or permanent differences in the
                  appropriate columns.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 15: Cost of Goods Sold
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the Cost of Goods Sold (COGS) reflected in the
                  financial statements and tax return. Include any
                  reconciliation adjustments required.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 16: Sale Versus Lease
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report adjustments for transactions treated differently as a
                  sale or a lease for book and tax purposes. Enter any related
                  reconciliation differences.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 17: Section 481(a) Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any Section 481(a) adjustment resulting from a change
                  in accounting method. Include the amount recognized for the
                  current tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 18: Unearned/Deferred Revenue
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report differences related to unearned or deferred revenue
                  between financial statements and the tax return. Enter the
                  required reconciliation adjustment.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 19: Income Recognition From Long-Term Contracts
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income recognized from long-term contracts. Include any
                  differences between financial statement reporting and tax
                  reporting.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 20: Original Issue Discount (OID) and Other Imputed
                  Interest
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report Original Issue Discount (OID) and other imputed
                  interest recognized during the tax year. Record any applicable
                  reconciliation differences.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21a: Income Statement Gain (Loss) on Disposition of
                  Assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the gain or loss recorded in the financial statements
                  from the disposition of assets, excluding inventory and
                  pass-through entities. Include any related reconciliation
                  adjustments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21b: Gross Capital Gains
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter gross capital gains reported on Schedule D, excluding
                  amounts from pass-through entities. Complete the applicable
                  reconciliation columns.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21c: Gross Capital Losse
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report gross capital losses from Schedule D, excluding
                  pass-through entity amounts, abandonment losses, and worthless
                  stock losses. Record any applicable differences.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21d: Net Gain (Loss) Reported on Form 4797
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the net gain or loss reported on Form 4797, Line 17,
                  excluding specified items. Include any temporary or permanent
                  differences.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21e: Abandonment Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report deductible abandonment losses recognized during the tax
                  year. Include any reconciliation adjustments, if required.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21f: Worthless Stock Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report deductible worthless stock losses and attach a
                  supporting statement. Record any related book-to-tax
                  differences.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21g: Other Gain (Loss) on Disposition of Assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any other gains or losses from the disposition of
                  assets not reported on the previous lines. Include the
                  required reconciliation adjustments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 22: Other Income (Loss) Items With Differences
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any other income or loss items that create book-to-tax
                  differences and are not reported elsewhere. Attach a
                  supporting statement describing each item.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 23: Total Income (Loss) Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine Lines 1 through 22 and enter the total income (loss).
                  The total should reflect all reconciliation adjustments
                  reported in Part II.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 24: Total Expense/Deduction Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total expense and deduction amount from{' '}
                  <span style={{ fontWeight: 600 }}>Part III, Line 32</span>.
                  Ensure the amount agrees with the corresponding
                  reconciliation.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 25: Other Items with No Differences
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report items that have no difference between the financial
                  statements and the tax return. These amounts require no
                  reconciliation adjustment.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 26: Reconciliation Totals
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Combine Lines 23 through 25 to determine the final
                  reconciliation totals.{' '}
                  <span style={{ fontWeight: 600 }}>Column (a)</span> must equal{' '}
                  <span style={{ fontWeight: 600 }}> Part I, Line 11,</span>
                  and <span style={{ fontWeight: 600 }}>Column (d)</span> must
                  equal{' '}
                  <span style={{ fontWeight: 600 }}>
                    Form 1120-S, Schedule K, Line 18.
                  </span>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part3"
                >
                  Part III: Reconciliation of Expense/Deduction Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schm3-1120s-part2-img.webp" />
                  <source srcSet="/assets/images/schm3-1120s-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schm3-1120s-part2-img.png"
                    loading="lazy"
                    alt="Schedule M-3 Part III showing reconciliation of expense and deduction items"
                    width={760}
                    height={781}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b>
                  <i>
                    {' '}
                    Report{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      expense amounts in Column (a)
                    </span>{' '}
                    as positive amounts and{' '}
                    <span style={{ fontWeight: 600 }}>
                      tax deductions in Column (d)
                    </span>{' '}
                    as positive amounts. The amounts reported on{' '}
                    <span style={{ fontWeight: 600 }}>Part II, Line 24</span>{' '}
                    must be the{' '}
                    <span style={{ fontWeight: 600 }}> opposite sign</span> of
                    the totals reported on{' '}
                    <span style={{ fontWeight: 600 }}> Part III, Line 32</span>.
                  </i>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Lines 1–6: Income Tax Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report current and deferred federal, state, local, and foreign
                  income tax expenses in the applicable lines. If your financial
                  statements do not separate current and deferred taxes, report
                  the entire amount as current tax expense.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 7: Equity-Based Compensation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report expenses related to equity-based compensation, such as
                  stock options, restricted stock, ESPPs, stock appreciation
                  rights, and similar awards. Include only amounts not reported
                  elsewhere on Schedule M-3.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 8: Meals and Entertainment
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report all meal, beverage, and entertainment expenses included
                  in your financial statements, regardless of how they are
                  classified. Do not include amounts already reported on another
                  Schedule M-3 line.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 9: Fines and Penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report fines and penalties paid or accrued for violating any
                  law, including civil or criminal penalties. Also report any
                  taxable recovery of previously recorded fines in the
                  appropriate column.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b>
                  <i>
                    {' '}
                    Do not include judgments, settlements, or damages here.
                    Report those on{' '}
                    <span style={{ fontWeight: 600 }}> Line 10</span>.
                  </i>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 10: Judgments, Damages, Awards, and Similar Costs
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report judgments, settlements, damages, awards, and similar
                  costs recorded in your financial statements. Include both
                  estimated and actual amounts, along with any related
                  book-to-tax adjustments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 11: Pension and Profit-Sharing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report expenses related to pension, profit-sharing, and other
                  qualified retirement plans. Include only amounts not reported
                  elsewhere on Schedule M-3.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 12: Other Post-Retirement Benefits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report expenses for retiree benefits other than pensions, such
                  as retiree health, dental, or life insurance coverage.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 13: Deferred Compensation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report compensation expenses recognized for financial
                  reporting but not currently deductible for tax purposes. Also
                  report compensation deducted on the tax return but not
                  included in book income for the current year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 14: Charitable Contribution of Cash and Tangible Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report charitable contributions of cash and tangible property.
                  Include any differences between the financial statement
                  expense and the tax deduction.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 15: Charitable Contribution of Intangible Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report charitable contributions of intangible property, such
                  as patents, copyrights, trademarks, securities, conservation
                  easements, or mineral rights.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Lines 16–18: Acquisition or Reorganization Costs
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report investment banking fees, legal and accounting fees, and
                  other acquisition or reorganization costs incurred during the
                  year. Include costs related to acquisitions, reorganizations,
                  spin-offs, liquidations, or initial public offerings.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 19: Amortization or Impairment of Goodwill
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report goodwill amortization or impairment losses recognized
                  during the tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 20: Amortization of Acquisition, Reorganization, and
                  Start-Up Costs
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report amortization related to acquisition, reorganization,
                  and start-up costs. Include amounts amortized under the
                  applicable tax provisions.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 21: Other Amortization or Impairment Write-Offs
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report amortization or impairment expenses not reported
                  elsewhere in Part III.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 22: Reserved
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is reserved by the IRS and should be treated as zero
                  when completing Schedule M-3 or related worksheets.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Lines 23a–23b: Depletion
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report depletion expenses for{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    oil and gas properties
                  </span>{' '}
                  on Line 23a and{' '}
                  <span style={{ fontWeight: 600 }}>
                    all other depletion expenses
                  </span>{' '}
                  on Line 23b, if not reported elsewhere.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 24: Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report depreciation expenses not already included on another
                  Schedule M-3 line. Include any book-to-tax differences in the
                  appropriate columns.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 25: Bad Debt Expense
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report bad debt expenses recognized in your financial
                  statements and the deductible bad debt amount allowed for tax
                  purposes under{' '}
                  <span style={{ fontWeight: 600 }}> Section 166</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 26: Interest Expense
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report total interest expense from your financial statements
                  and the corresponding tax deduction. Complete{' '}
                  <span style={{ fontWeight: 600 }}>Form 8916-A</span> if
                  required and report any book-to-tax adjustments in the
                  appropriate columns.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b>
                  <i>
                    {' '}
                    Corporations with{' '}
                    <span style={{ fontWeight: 600 }}>
                      less than $50 million in total assets
                    </span>{' '}
                    are generally{' '}
                    <span style={{ fontWeight: 600 }}> not required</span> to
                    file Form 8916-A but may choose to file it voluntarily.
                  </i>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 27: Corporate-Owned Life Insurance Premiums
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report premiums paid for corporate-owned life insurance
                  policies where the corporation is directly or indirectly the
                  beneficiary. Report only the deductible portion in Column (d).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 28: Purchase Versus Lease
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report adjustments when a transaction is treated as a{' '}
                  <span style={{ fontWeight: 600 }}> lease</span>
                  for financial reporting but as a{' '}
                  <span style={{ fontWeight: 600 }}> purchase</span> for tax
                  purposes, or vice versa. Include related depreciation and
                  interest adjustments on the appropriate lines.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 4,
                    backgroundColor: '#EDCBF8',
                    borderLeft: '3px solid #6D207D',
                    color: '#111',
                  }}
                >
                  <b>Example:</b>
                  <i>
                    {' '}
                    If rent expense is reported in the financial statements but
                    the transaction is treated as a purchase for tax purposes,
                    report rent on this line and report the related depreciation
                    and interest on{' '}
                    <span style={{ fontWeight: 600 }}>Lines 24 and 26</span>.
                  </i>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 29 – Research and Development Costs
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report research and development expenses recognized in your
                  financial statements and the corresponding tax deduction under
                  <span style={{ fontWeight: 600 }}> Section 174.</span> Include
                  temporary or permanent differences where applicable.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 30: Section 118 Exclusion
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any non-shareholder capital contributions or
                  inducements received during the year. Attach a statement
                  describing the property, cash, or tax credits received, even
                  if no amount is reported.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 31: Other Expense/Deduction Items With Differences
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any expense or deduction not reported elsewhere that
                  creates a book-to-tax difference. Attach a statement
                  describing each item and showing the amounts reported in
                  Columns (a) through (d).
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note:</b>
                  <i>
                    {' '}
                    Include reserves, contingent liabilities, prepaid expenses,
                    or other adjustments that are not specifically reported on
                    another Schedule M-3 line.
                  </i>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Line 32: Total Expense/Deduction Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Combine{' '}
                  <span style={{ fontWeight: 600 }}> Lines 1 through 31</span>{' '}
                  and enter the total expense or deduction. Report the{' '}
                  <span style={{ fontWeight: 600 }}> opposite sign</span> of
                  these amounts on{' '}
                  <span style={{ fontWeight: 600 }}>Part II, Line 24,</span> as
                  required by the IRS.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                  id="structure"
                >
                  Schedule M-3 vs Schedule M-1
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Feature
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Schedule M-1
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Schedule M-3
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Purpose{' '}
                          </TableCell>
                          <TableCell>
                            {' '}
                            Provides a summary-level reconciliation of income
                            (loss) per books with income (loss) per tax return
                          </TableCell>
                          <TableCell>
                            Provides a detailed reconciliation of financial
                            statement income with taxable income{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#cccccc3e' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Reporting Detail{' '}
                          </TableCell>
                          <TableCell>
                            {' '}
                            Reports overall book-to-tax differences
                          </TableCell>
                          <TableCell>
                            Reports differences line by line with more detailed
                            information
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who Uses It{' '}
                          </TableCell>
                          <TableCell>
                            {' '}
                            Generally used by S corporations that are not
                            required to file Schedule M-3
                          </TableCell>
                          <TableCell>
                            Required for certain S corporations with total
                            assets of $10 million or more; may also be filed
                            voluntarily by smaller S corporations
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#cccccc3e' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            IRS Reporting{' '}
                          </TableCell>
                          <TableCell>
                            {' '}
                            Provides basic information about differences between
                            book and tax reporting
                          </TableCell>
                          <TableCell>
                            Provides greater transparency and detailed
                            information for IRS review
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Complexity{' '}
                          </TableCell>
                          <TableCell> Simpler reconciliation process</TableCell>
                          <TableCell>
                            Requires more detailed financial and tax information
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="book"
                >
                  Common Book-to-Tax Adjustments Seen on Schedule M-3
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Frequent reconciliation items include:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>Depreciation differences between book and tax</li>
                  <li>Deferred revenue adjustments</li>
                  <li>Interest expense limitations</li>
                  <li>Equity compensation timing</li>
                  <li>Goodwill impairment vs tax amortization</li>
                  <li>Long-term contract revenue recognition</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These adjustments explain why financial statement income
                  differs from taxable income.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Filing Mistakes to Avoid
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Businesses often encounter issues such as:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    Mixing income and expense items between Parts II and III
                  </li>
                  <li>Failing to attach required supporting statements</li>
                  <li>
                    Leaving temporary or permanent difference columns incomplete
                  </li>
                  <li>Not reconciling Part I totals with Part II totals</li>
                  <li>Incorrect treatment of disregarded entities or QSubs</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Because Schedule M-3 is highly structured, incomplete
                  reconciliation may trigger IRS inquiries.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="faq"
                >
                  FAQs About Schedule M-3 (Form 1120-S)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. Is Schedule M-3 required every year once the asset
                  threshold is met?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes. If total assets reach or exceed $10 million, Schedule M-3
                  generally replaces Schedule M-1 for that tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. Can a corporation choose to file Schedule M-3 voluntarily?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes. Some corporations file Schedule M-3 below the threshold
                  to maintain consistent reconciliation reporting.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. Why does Schedule M-3 require temporary and permanent
                  difference columns?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These columns allow the IRS to distinguish between timing
                  differences that reverse later and permanent adjustments that
                  permanently affect taxable income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. Do disregarded entities need separate reporting on Schedule
                  M-3?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Generally no. Their activity is included through the parent S
                  corporation unless specific adjustments require disclosure.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to e-file Form 1120-S with Schedule M-3?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Streamline your S corporation tax compliance today.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=schm3`}
              >
                Start e-Filing Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default ScheduleM3Form1120S;
